import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];
  #abortController;

  connectedCallback() {
		super.connectedCallback();

		const { videoSources, videoElement } = this.refs;

		/**
		 * sets the autoplay-critical flags in JS before loading the dynamic sources
		 */
		if (!(videoElement instanceof HTMLVideoElement)) return;
		this.#abortController?.abort();
		this.#abortController = new AbortController();
		const signal = this.#abortController.signal;

		videoElement.muted = true;
		videoElement.defaultMuted = true;
		videoElement.autoplay = true;
		videoElement.loop = true;
		videoElement.playsInline = true;

		const attemptAutoplay = () => {
			const playAttempt = videoElement.play();

			if (playAttempt && typeof playAttempt.catch === 'function') {
				playAttempt.catch(() => {});
			}
		};

		for (const source of videoSources) {
			const { videoSource } = source.dataset;

			if (videoSource) source.setAttribute('src', videoSource);
		}

		/*
		 * Explicitly calls video.play() once the video is ready
		 */
		videoElement.addEventListener('loadeddata', attemptAutoplay, { once: true, signal });
		videoElement.addEventListener('canplay', attemptAutoplay, { once: true, signal });
		videoElement.load();

		if (videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
			attemptAutoplay();
		}
	}

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
